import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Loader2 } from 'lucide-react';

// Peças de UI compartilhadas pelas 4 telas do Dimensionamento de Escala.
//
// ⚠️ Este arquivo existe para QUEBRAR UM CICLO: a lista importa o editor, e o
//    editor precisava dos estilos que moravam na lista. Import circular em
//    React não costuma explodir — ele entrega `undefined` em silêncio, e o
//    sintoma é um estilo que some sem erro no console. Melhor não ter o ciclo.

export const COR_MODULO = '#d81d1d';   // vermelho do RH

export const btn = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '6px 10px', background: bg, color, border: 'none',
    borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
});

export const btnVoltar = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
    borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

export const sel = {
    padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8,
    fontWeight: 700, fontSize: 13, background: '#fff',
};

export const aviso = (bg, color) => ({
    background: bg, color, padding: '10px 14px', borderRadius: 8,
    fontSize: 13, marginBottom: 12, lineHeight: 1.5,
});

export const tabela = { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' };
export const th = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontSize: 11, textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' };
export const thNum = { ...th, textAlign: 'right' };
export const td = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };
export const tdNum = { ...td, textAlign: 'right' };

// ---------------------------------------------------------------------
// ⏳ SINAL DE "ESTOU FAZENDO" — o giro
// ---------------------------------------------------------------------
// ⚠️⚠️ ISTO NÃO É ENFEITE, e a falta dele já custou confiança na ferramenta.
//    Rodrigo, 15/09: "cliquei pra excluir um registro, ele não mostrou NADA,
//    achei que não tinha feito nada; aí atualizei, o registro continuava lá, e
//    depois sumiu."
//
//    A causa é que o banco é REMOTO (a VPS) — uma escrita leva de centenas de
//    milissegundos a alguns segundos. Sem sinal nenhum na tela, esse intervalo
//    é indistinguível de "o clique não pegou", e a reação natural é clicar de
//    novo ou recarregar no meio da operação. Com DELETE em cascata, clicar duas
//    vezes é pedir a exclusão de coisa que já não existe.
//
//    A regra do módulo, daqui para a frente: TODA ação que vai ao servidor
//    mostra que está acontecendo (este giro), trava o próprio botão enquanto
//    acontece, e ao terminar DIZ o que fez.
const Girando = ({ size = 16 }) => (
    <>
        {/* O keyframes vai junto: são telas sem CSS próprio, tudo é estilo inline. */}
        <style>{'@keyframes escalaGira { to { transform: rotate(360deg) } }'}</style>
        <Loader2 size={size} style={{ animation: 'escalaGira .9s linear infinite' }} />
    </>
);
export { Girando };

// ---------------------------------------------------------------------
// Botão de ação só com ÍCONE
// ---------------------------------------------------------------------
// Padrão da tabela de usuários do Painel Admin (pedido do Rodrigo, 14/09):
// sem rótulo, cor sugestiva, e a função aparece no `title` ao passar o mouse.
// ⚠️ O `title` NÃO é enfeite — sem ele a fileira de ícones vira adivinhação.
// ⚠️ `ocupado` troca o ícone pelo giro E desabilita o botão: os dois juntos, e
//    não um só. O giro sozinho não impede o segundo clique; o disabled sozinho
//    não explica por que o botão parou de responder.
export const IconeAcao = ({ onClick, cor, titulo, children, desabilitado, ocupado }) => (
    <button onClick={onClick} title={ocupado ? 'Executando…' : titulo} disabled={desabilitado || ocupado}
        style={{
            background: 'none', border: 'none', padding: 3, marginRight: 2,
            cursor: desabilitado || ocupado ? 'not-allowed' : 'pointer',
            color: desabilitado ? '#cbd5e1' : cor, opacity: desabilitado ? 0.4 : 1,
            verticalAlign: 'middle', lineHeight: 0,
        }}>
        {ocupado ? <Girando size={17} /> : children}
    </button>
);

// ---------------------------------------------------------------------
// Ordenação por clique no cabeçalho
// ---------------------------------------------------------------------
// `useOrdenacao` devolve a lista já ordenada e o que o cabeçalho precisa para
// se desenhar. Uso:
//
//   const { linhas, cabecalho } = useOrdenacao(dados, 'LOJA');
//   <th {...cabecalho('LOJA')}>Loja</th>
//
// ⚠️ Texto ordena com `localeCompare(pt-BR)` — sem isso "Ávila" cai depois de
//    "Zurique", porque a comparação crua usa o código do caractere.
// ⚠️ Número ordena como NÚMERO. Comparar '10' com '9' como texto põe o 10
//    antes — é o bug de ordenação mais comum que existe.
export function useOrdenacao(linhas, campoInicial = null, direcaoInicial = 'asc') {
    const [ordem, setOrdem] = useState({ campo: campoInicial, direcao: direcaoInicial });

    const ordenadas = useMemo(() => {
        if (!ordem.campo) return linhas;
        const sinal = ordem.direcao === 'asc' ? 1 : -1;
        return [...linhas].sort((a, b) => {
            const x = a?.[ordem.campo];
            const y = b?.[ordem.campo];
            // Vazio sempre por último, independentemente da direção: uma coluna
            // de nulos no topo esconde justamente o que se foi ordenar para ver.
            const vazio = (v) => v === null || v === undefined || v === '';
            if (vazio(x) && vazio(y)) return 0;
            if (vazio(x)) return 1;
            if (vazio(y)) return -1;
            if (typeof x === 'number' && typeof y === 'number') return (x - y) * sinal;
            const nx = Number(x); const ny = Number(y);
            if (!Number.isNaN(nx) && !Number.isNaN(ny) && String(x).trim() !== '' && String(y).trim() !== '') {
                return (nx - ny) * sinal;
            }
            return String(x).localeCompare(String(y), 'pt-BR', { sensitivity: 'base' }) * sinal;
        });
    }, [linhas, ordem]);

    const clicar = (campo) => setOrdem((o) => ({
        campo,
        // 3º clique no mesmo campo volta ao natural — permite desfazer sem
        // precisar recarregar a tela.
        direcao: o.campo !== campo ? 'asc' : o.direcao === 'asc' ? 'desc' : 'asc',
    }));

    const cabecalho = (campo, estiloBase = th) => ({
        onClick: () => clicar(campo),
        style: { ...estiloBase, cursor: 'pointer', userSelect: 'none' },
        title: 'Clique para ordenar',
    });

    const Seta = ({ campo }) => {
        const ativo = ordem.campo === campo;
        const Icone = !ativo ? ChevronsUpDown : ordem.direcao === 'asc' ? ChevronUp : ChevronDown;
        return <Icone size={12} style={{ verticalAlign: 'middle', marginLeft: 3, opacity: ativo ? 0.9 : 0.3 }} />;
    };

    return { linhas: ordenadas, ordem, cabecalho, Seta };
}

// Cabeçalho ESCURO com letra branca — pedido do Rodrigo (14/09): o cinza sobre
// branco do padrão anterior sumia na tela.
// ⚠️ `color` no `th` não basta: a seta de ordenação é um SVG que herda
//    `currentColor`, então a cor precisa estar aqui e não só no texto.
export const thEscuro = {
    textAlign: 'left', padding: '10px 12px', whiteSpace: 'nowrap',
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
    color: '#fff', fontWeight: 700, background: '#0f172a', border: 'none',
};
export const thEscuroNum = { ...thEscuro, textAlign: 'right' };
export const thEscuroCentro = { ...thEscuro, textAlign: 'center' };
